import { translate } from '@jsverse/transloco';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { ProjectService } from 'app/providers/storage-api/project.service';
import { ReportService } from 'app/providers/storage-api/report.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { AbstractFeatureChartReport } from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';

export class WordcloudChartReport extends AbstractFeatureChartReport {
  reportName = 'featureSentimentScore';
  reportHandler = ChartDataService.forDocumentCount;
  canExportAsPptx = false;
  sentimentRanges;

  constructor(
    public options: WidgetChartOptions,
    protected reportService: ReportService,
    protected filterService: FilterService,
    protected comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService,
    private projectService: ProjectService,
    public metadataType: string
  ) {
    super(options, reportService, filterService, comparisonService, timezoneService, metadataType);
  }

  getFields() {
    let fields = super.getFields();

    fields = fields.filter((x) => {
      return ['aggregator', 'graphType'].indexOf(x) < 0;
    });

    return fields;
  }

  isAnalyticItemSupported(analyticItem: string) {
    return analyticItem !== 'documents';
  }

  isAnalyticItemRequired() {
    return true;
  }

  canDrilldownMentions() {
    return true;
  }

  canDrilldownThemes() {
    return true;
  }

  canSort() {
    return false;
  }

  public canSetChartMaximum(): boolean {
    return false;
  }

  async processResponse(response, _filterQuery?: string) {
    if (!this.sentimentRanges) {
      const project = await this.projectService.getProjectById(this.options.projectId, false);
      const analyticItem = this.options.analyticItem === 'documents' ? 'document' : 'feature';
      this.sentimentRanges = project.polarities[analyticItem][this.options.sentimentScale];
    }

    const resultExtractor = (bucketedResponse) => {
      const result = { items: { count: {} } };

      switch (bucketedResponse.reportName) {
        case this.reportName:
          this.options.orderBySentiment = false;

          const documentItems = bucketedResponse.items.docCount;
          const sentimentItems = bucketedResponse.items.count;

          for (const featureName of Object.getOwnPropertyNames(documentItems)) {
            result.items.count[featureName] = {
              documentCount: documentItems[featureName],
            };

            if (sentimentItems[featureName] !== undefined) {
              result.items.count[featureName].sentimentScore = sentimentItems[featureName];
            }
          }

          return result;
        case 'featureTopMentions':
          for (const featureName of Object.getOwnPropertyNames(bucketedResponse.items.count)) {
            result.items.count[featureName] = {
              sentimentScore: 0,
              documentCount: bucketedResponse.items.count[featureName],
            };
          }

          return result;
        case 'featurePhrases':
          for (const featureName of Object.getOwnPropertyNames(bucketedResponse.items.count)) {
            result.items.count[featureName] = {
              sentimentScore: bucketedResponse.items.sentiment[featureName],
              documentCount: bucketedResponse.items.count[featureName],
            };
          }

          return result;
        case 'featureTopThemes':
          for (const featureName of Object.getOwnPropertyNames(bucketedResponse.items)) {
            let count = 0;
            let score = 0;

            for (const key of Object.getOwnPropertyNames(bucketedResponse.items[featureName])) {
              const docCount = bucketedResponse.items[featureName][key];

              switch (key) {
                case 'negative':
                  score -= 0.5 * docCount;
                  break;
                case 'very negative':
                  score -= 1 * docCount;
                  break;
                case 'positive':
                  score += 0.5 * docCount;
                  break;
                case 'very positive':
                  score += 1 * docCount;
                  break;
              }

              count += docCount;
            }

            result.items.count[featureName] = {
              sentimentScore: count > 0 ? score / count : 0,
              documentCount: count,
            };
          }

          return result;
        default:
          return result;
      }
    };

    const result = resultExtractor(response);

    if (response.comparisonBuckets?.length > 1) {
      const otherResult = resultExtractor(response.comparisonBuckets[1]);

      for (const featureName of Object.keys(result.items.count)) {
        const resultCount = result.items.count[featureName];
        const otherResultCount = otherResult.items.count[featureName] ?? {
          sentimentScore: 0,
          documentCount: 0,
        };

        resultCount.documentCount = Math.abs(
          resultCount.documentCount - otherResultCount.documentCount
        );
      }

      for (const featureName of Object.keys(otherResult.items.count).filter(
        (k) => !result.items.count[k]
      )) {
        result.items.count[featureName] = otherResult.items.count[featureName];
      }
    }

    result['extra'] = response['extra'];
    result['reportName'] = response['reportName'];
    return result;
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const series = options.xAxis.categories;
    const sortedWeights = series.map((x) => response.items.count[x].documentCount);
    const data = series.reduce((arr, word) => {
      let result = arr.find((obj) => obj.name === word);

      result = series.map((name, index) => {
        const item = response.items.count[name];
        if (typeof item.sentimentScore !== 'undefined') {
          return {
            name,
            weight: item.documentCount,
            sentimentScore: item.sentimentScore || 0,
            selected: item.selected,
            documentCount: item.documentCount,
          };
        }
        return {
          name,
          weight: sortedWeights[index],
          selected: item.selected,
          documentCount: item.documentCount,
        };
      });

      return result;
    }, []);

    for (const item of data) {
      item.color = AbstractFeatureChartReport.getSentimentColor(
        this.sentimentRanges,
        item.sentimentScore
      );
    }

    options.series[0].data = data;
    options.series[0].name = 'Count';
    options.series[0].rotation = { from: 0, to: 0 };
    options.series[0].spiral = 'rectangular';
    options.series[0].placementStrategy = 'center';
    options.series[0].tooltip = {
      headerFormat: `${translate('widgets.charting.tooltip.name-part', {
        name: "'{point.key'}",
      })}<br/>`,
      pointFormatter() {
        return `<b>${this.series.name}: </b> ${Utils.addThousandSeparator(this.weight)}`;
      },
    };
    options.chart = { type: 'wordcloud' };

    return options;
  }
}

export class MetadataWordcloudChartReport extends WordcloudChartReport {
  reportName = 'metadataSentimentScore';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const formatterFn = this.formatMetadataValueFactory;

    ret.series[0].data.forEach((word) => {
      word.name = formatterFn(word.name);
    });

    return ret;
  }
}

export class TaxonomyWordCloudChartReport extends WordcloudChartReport {
  reportName = 'taxonomySentimentScore';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    ret['taxonomyNodes'] = response.extra['taxonomyNodes'];
    for (const item of ret.series[0].data) {
      if (item.name) {
        const taxonomyNode = ret['taxonomyNodes'][item.name];
        if (taxonomyNode) {
          item['pointId'] = item.name;
          item['pointDisplay'] = taxonomyNode.name;
          item['name'] = taxonomyNode.name;
        }
      }
    }
    ret.tooltip.formatter = function () {
      const id = this.point.pointId || this.key;
      const taxonomyNode = ret['taxonomyNodes'][id];
      const name = taxonomyNode ? taxonomyNode['name'] : id;
      const path = taxonomyNode ? taxonomyNode['path'] : '';
      return `${translate('widgets.charting.tooltip.name-part', { name })}<br>
        ${translate('widgets.charting.tooltip.path-part', { path })}<br>
        ${translate('widgets.charting.tooltip.count-part', {
          count: Utils.addThousandSeparator(this.point.weight),
        })}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      return ret['taxonomyNodes'][this.value]
        ? ret['taxonomyNodes'][this.value]['name']
        : this.value;
    };
    return ret;
  }
}
